import { Component, OnInit, inject, signal, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { ApiConfiguration } from 'src/app/services/http/api-configuration';
import { ApiService } from 'src/app/services/http/api.service';
import { AuthenticationCheckService } from 'src/app/services/auth/authentication-check.service';
import { ToastClientService } from 'src/app/services/toast/toast-client.service';

@Component({
  selector: 'app-signin',
  templateUrl: './signin.component.html',
  styleUrls: ['./signin.component.scss'],
  standalone: false,
})
export class SigninComponent implements OnInit {
  // Dependency injection using inject() function - Angular 14+ pattern
  private readonly apiService = inject(ApiService);
  private readonly apiConfig = inject(ApiConfiguration);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(AuthenticationCheckService);
  private readonly toast = inject(ToastClientService);
  private readonly destroyRef = inject(DestroyRef);

  // Reactive signals for state management (Angular 16+)
  readonly errorMessage = signal<boolean>(true);
  readonly submitted = signal<boolean>(false);
  readonly isLoading = signal<boolean>(false);

  // Form groups with proper typing
  loginForm!: FormGroup;

  constructor() {
    this.initializeForms();
    this.checkExistingLogin();
  }

  ngOnInit(): void {
    // Program loading now happens in programs-list component
  }

  // Form control getters for template access
  get c() {
    return this.loginForm.controls;
  }

  /**
   * Initialize login form with validators
   */
  private initializeForms(): void {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
    });
  }

  /**
   * Check if user is already logged in and redirect to previous URL
   */
  private checkExistingLogin(): void {
    const previousURL = localStorage.getItem('previousURL');
    const isLoggedIn = localStorage.getItem('login') === 'true';

    if (isLoggedIn && previousURL) {
      this.router.navigateByUrl(previousURL);
    }
  }

  /**
   * Handle login form submission
   * New flow: authenticate with email + password, then load programs
   */
  login(formData: { email: string; password: string }): void {
    this.submitted.set(true);

    if (this.loginForm.invalid) {
      return;
    }

    this.isLoading.set(true);

    // Clear ALL old auth data first
    localStorage.clear();

    const loginPayload = {
      LoginForm: {
        program_id: 1, // Hardcoded as per new requirement
        email: formData.email,
        password: formData.password,
        login_type: 'web',
        portal_type: 'manager',
      },
    };

    console.log('🔵 Login payload:', loginPayload);

    this.apiService
      .post(this.apiConfig.login, JSON.stringify(loginPayload))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.handleLoginSuccess(res, formData.email, formData.password);
        },
        error: () => {
          this.errorMessage.set(false);
          this.isLoading.set(false);
        },
      });
  }

  /**
   * Handle successful login response
   * New flow: Save user data and navigate to program selection
   *
   * API Response Structure:
   * {
   *   success: true,
   *   status: 200,
   *   data: {
   *     id: 6,
   *     jwt_token: "...",
   *     user_name: "ayush",
   *     user_role: 1,
   *     program_id: 1,
   *     role_name: 1, // Note: This is a number, not a string
   *     login_type: "web",
   *     token_expires_at: "2026-05-13 15:50:37",
   *     token_expiry_seconds: 2592000
   *   }
   * }
   */
  private handleLoginSuccess(res: any, email: string, password: string): void {
    if (res.status !== 200) {
      this.isLoading.set(false);
      return;
    }

    this.errorMessage.set(true);
    const userData = res.body.data;

    // Don't show toast here - will show on programs list page

    // Store user data (without program context yet)
    // Note: role_name is a number in the response, not a string
    localStorage.setItem('user_name', userData.user_name || email);
    localStorage.setItem('user_id', String(userData.id));
    localStorage.setItem('user_role', String(userData.user_role));
    localStorage.setItem('role_name', String(userData.role_name));
    localStorage.setItem('login_details', JSON.stringify(res.body.data));

    // Store email for program selection page to use
    localStorage.setItem('user_email', email);

    // Save authentication token - API returns jwt_token
    // Save as both master token (for get-programs) and auth token (for other APIs)
    this.auth.saveMasterToken(userData.jwt_token);
    this.auth.saveAuth(userData.jwt_token);

    this.submitted.set(false);
    this.isLoading.set(false);

    // Navigate to program selection page
    // Programs will be loaded by the programs-list component
    this.router.navigate(['/select-program']);
  }

  /**
   * Check if form field is valid for styling
   */
  isValid(field: string): boolean {
    const control = this.loginForm.controls[field];

    // Untouched fields are considered valid (initial state)
    if (!control.touched) {
      return true;
    }

    return control.touched && control.valid;
  }
}
